import React, { useEffect } from 'react'
import update from 'immutability-helper'
import PropTypes from 'prop-types'
import { ALLOWED_IMAGE_CONTENT_TYPES } from 'core/common/constants/settings'
import { isFunction } from 'core/common/utils/is'
import { createMedia, createMediaURL, removeMedia } from 'core/services/http'

const UploadStateContext = React.createContext()
const UploadDispatchContext = React.createContext()

const TYPE = {
  ADD_FILES: 'ADD_FILES',
  UPDATE_FILE: 'UPDATE_FILE',
  UPDATE_FILES: 'UPDATE_FILES',
  REPLACE_FILES: 'REPLACE_FILES',
  SET_UPLOAD_STATUS: 'STOPPED',
  REMOVE_FILE: 'REMOVE_FILE',
  MOVE_FILE: 'MOVE_FILE',
  SELECT_FILE: 'SELECT_FILE',
  SET_IS_SELECTABLE: 'SET_IS_SELECTABLE',
  SET_COVER: 'SET_COVER',
  RESET_FILES_STATE: 'RESET_FILES_STATE',
}

const INITIAL_STATE = {
  FILES: [],
  ACCEPT: ALLOWED_IMAGE_CONTENT_TYPES,
  REMOVE_FILE: null,
  UPLOAD_STATUS: 'STOPPED',
  MAX_SELECT_FILES: 10,
  TOTAL_SELECT_FILES: 0,
  IS_SELECTABLE: false,
  IS_EDITING: false,
}

const FILE = {
  id: null,
  id_ref: null,
  file: null,
  src: null,
  url: null,
  cover: false,
  percentage: 0,
  uploaded: false,
  uploadStatus: null,
  selected: false,
  error: false,
}

const statusFile = {
  waiting: fileData => ({
    ...FILE,
    ...fileData,
    id_ref: fileData.id,
    uploadStatus: 'waiting',
    percentage: 0,

    error: false,
  }),
  uploading: (fileData, progressEvent) => ({
    ...FILE,
    ...fileData,
    id_ref: fileData.id,
    uploadStatus: 'uploading',
    percentage: progressEvent ? (progressEvent.loaded / progressEvent.total) * 100 : 0,

    error: false,
  }),
  done: (fileData, { data }) => ({
    ...FILE,
    ...fileData,
    src: null,
    uploaded: true,
    uploadStatus: 'done',
    id: data.id,
    id_ref: fileData.id,
    url: data.variations[0].url,
    percentage: 100,
    selected: true,
    error: false,
  }),
  error: (fileData, error) => ({
    ...FILE,
    ...fileData,
    id_ref: fileData.id,
    uploadStatus: 'error',
    percentage: 0,
    selected: false,
    error,
  }),
}

const updateFile = (prev, current) => {
  return prev.map(file => {
    if (file.id_ref === current.id_ref) return current
    return file
  })
}

const updateFiles = (prev, current) => {
  return prev.reduce((prevFile, currentFile) => {
    const response = current.find(item => item.id_ref === currentFile.id_ref)
    if (response) return [...prevFile, response]
    return [...prevFile, currentFile]
  }, [])
}

const uploadReducer = (state, action) => {
  switch (action.type) {
    case TYPE.ADD_FILES: {
      const newFiles = [...state.FILES, ...action.state]
      const files = newFiles.map((file, index) => ({
        ...file,
        cover: index === 0,
      }))
      return { ...state, FILES: files }
    }
    case TYPE.UPDATE_FILE: {
      const FILES = updateFile(state.FILES, action.state)
      return { ...state, FILES }
    }
    case TYPE.UPDATE_FILES: {
      const FILES = updateFiles(state.FILES, action.state)
      return { ...state, FILES }
    }
    case TYPE.REPLACE_FILES: {
      const total = action.state.filter(file => file.selected === true).length
      return { ...state, FILES: action.state, TOTAL_SELECT_FILES: total }
    }
    case TYPE.RESET_FILES_STATE: {
      return { ...state, TOTAL_SELECT_FILES: 0, IS_SELECTABLE: false }
    }
    case TYPE.SET_UPLOAD_STATUS: {
      return { ...state, UPLOAD_STATUS: action.state }
    }
    case TYPE.MOVE_FILE: {
      const draggedImage = state.FILES[action.state.dragIndex]
      const rules = {
        $splice: [
          [action.state.dragIndex, 1],
          [action.state.hoverIndex, 0, draggedImage],
        ],
      }
      const filesOrdered = update(state.FILES, rules)

      let files = filesOrdered.map((file, index) => {
        if (index === 0) return { ...file, cover: true }
        return { ...file, cover: false }
      })

      if (state.IS_SELECTABLE) {
        files = filesOrdered.map((file, index) => {
          if (index === 0) return { ...file, selected: true, cover: true }
          return { ...file, cover: false }
        })
      }

      const total = files.filter(file => file.selected === true).length
      return { ...state, FILES: files, TOTAL_SELECT_FILES: total }
    }
    case TYPE.REMOVE_FILE: {
      const files = state.FILES.filter(file => file.id_ref !== action.state.id_ref)

      return { ...state, FILES: files }
    }
    case TYPE.SELECT_FILE: {
      const totalFilesSelected = state.FILES.filter(file => file.selected === true).length
      let totalCoverSelected = state.FILES.filter(file => file.cover === true).length
      const files = state.FILES.map(file => {
        const sameFile = file.id === action.state.id
        const limitSelect = totalFilesSelected + 1 <= state.MAX_SELECT_FILES

        //  remove select
        if (file.selected && sameFile) {
          if (file.cover) {
            totalCoverSelected = 0
          }
          return { ...file, selected: false, cover: false }
        }
        // select file
        if (!file.selected && sameFile && limitSelect)
          return {
            ...file,
            selected: true,
          }

        // change cover
        if (file.selected && totalCoverSelected === 0) {
          totalCoverSelected = 1
          return { ...file, cover: true }
        }

        return file
      })

      const findCover = files.find(file => file.cover === true)
      if (findCover) {
        const findIndex = files.findIndex(file => file.id === findCover.id)
        files.unshift(files.splice(findIndex, 1)[0])
      }

      const total = files.filter(file => file.selected === true).length
      return { ...state, FILES: files, TOTAL_SELECT_FILES: total }
    }

    case TYPE.SET_IS_SELECTABLE: {
      return { ...state, IS_SELECTABLE: action.state }
    }

    case TYPE.SET_COVER: {
      if (action.state.cover) {
        return state
      }
      const filesCover = state.FILES.map(file => {
        if (file.id === action.state.id) {
          if (state.IS_SELECTABLE) {
            return { ...file, cover: true, selected: true }
          }
          return { ...file, cover: true }
        }
        return { ...file, cover: false }
      })

      // reorder
      const findIndex = filesCover.findIndex(file => file.id === action.state.id)
      filesCover.unshift(filesCover.splice(findIndex, 1)[0])

      const total = filesCover.filter(file => file.selected === true).length
      return { ...state, FILES: filesCover, TOTAL_SELECT_FILES: total }
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`)
    }
  }
}

const useUploadState = () => {
  const context = React.useContext(UploadStateContext)
  if (context === undefined) {
    throw new Error('useUploadState must be used within a UploadProvider')
  }
  return context
}

const useUploadDispatch = () => {
  const context = React.useContext(UploadDispatchContext)
  if (context === undefined) {
    throw new Error('useCountDispatch must be used within a UploadProvider')
  }
  return context
}

const useUpload = () => [useUploadState(), useUploadDispatch()]

const UploadProvider = ({ children, initialImages, custom }) => {
  INITIAL_STATE.MAX_SELECT_FILES = custom ? custom.MAX_SELECT_FILES : INITIAL_STATE.MAX_SELECT_FILES
  const [state, dispatch] = React.useReducer(
    uploadReducer,
    initialImages.length > 0
      ? { ...INITIAL_STATE, FILES: initialImages, IS_EDITING: true }
      : INITIAL_STATE
  )

  const createURLPromise = async fileData => {
    const { url } = fileData
    const form = new FormData()
    form.append('url', url)
    form.append('fingerprint', '123456789132')
    try {
      const response = custom
        ? await custom.createCustomMediaURL({ form })
        : await createMediaURL({ form })
      dispatch({
        type: TYPE.UPDATE_FILE,
        state: statusFile.done(fileData, response),
      })
    } catch (e) {
      dispatch({
        type: TYPE.UPDATE_FILE,
        state: statusFile.error({ ...fileData, file: fileData }, e.response.data),
      })
    }
  }

  const createPromise = async fileData => {
    const { file } = fileData
    const form = new FormData()
    form.append('file', file, file.name)
    form.append('fingerprint', '123456789132')
    try {
      const progress = progressEvent => {
        if (progressEvent.lengthComputable) {
          dispatch({ type: TYPE.UPDATE_FILE, state: statusFile.uploading(fileData, progressEvent) })
        }
      }
      const response = custom
        ? await custom.createCustomMedia({ form, progress })
        : await createMedia({ form, progress })
      dispatch({ type: TYPE.UPDATE_FILE, state: statusFile.done(fileData, response) })
    } catch (e) {
      dispatch({ type: TYPE.UPDATE_FILE, state: statusFile.error(fileData, e.response.data) })
    }
  }

  const uploadFiles = async filesToUpload => {
    const filesUpdate = filesToUpload.map(fileData => statusFile.uploading(fileData))
    dispatch({ type: TYPE.UPDATE_FILES, state: filesUpdate })

    const promises = filesUpdate.map(fileData => {
      if (fileData.type === 'URL') {
        return createURLPromise(fileData)
      }

      return createPromise(fileData)
    })
    try {
      dispatch({ type: TYPE.SET_UPLOAD_STATUS, state: 'IN_PROGRESS' })
      await Promise.all(promises)
    } finally {
      dispatch({ type: TYPE.SET_UPLOAD_STATUS, state: 'COMPLETED' })
    }
  }

  useEffect(() => {
    const filesToUpload = state.FILES.filter(file => file.uploadStatus === 'waiting')
    if (filesToUpload.length) uploadFiles(filesToUpload)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.FILES])

  useEffect(() => {
    if (state.REMOVE_FILE) removeMedia({ id: state.REMOVE_FILE.id })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.REMOVE_FILE])

  return (
    // eslint-disable-next-line react/jsx-no-constructed-context-values
    <UploadStateContext.Provider value={state}>
      <UploadDispatchContext.Provider value={dispatch}>
        {isFunction(children) ? children(state) : children}
      </UploadDispatchContext.Provider>
    </UploadStateContext.Provider>
  )
}

export { UploadProvider, useUpload, useUploadState, useUploadDispatch, TYPE as actionsUpload }

UploadProvider.defaultProps = {
  children: null,
  initialImages: [],
}

UploadProvider.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  initialImages: PropTypes.arrayOf(PropTypes.object),
}
